import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";

const products = [
  {
    model: "SC880 5G",
    badge: "Flagship",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/0cb7f09f1fcbcac9b09d7997834340e7.png",
    specs: [
      { icon: "ri-4k-line", label: "4K Video" },
      { icon: "ri-signal-wifi-3-line", label: "5G LTE" },
      { icon: "ri-time-line", label: "10-hr Battery" },
      { icon: "ri-shield-check-line", label: "IP68 / MIL-810G" },
    ],
    highlights: ["300s pre-record buffer", "Starlight night vision", "GPS + Wi-Fi 6", "Live streaming"],
    desc: "The ultimate 5G body camera for frontline law enforcement. 4K clarity, 300-second pre-record, and real-time command centre streaming.",
  },
  {
    model: "SC780 4G",
    badge: "Popular",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/7429248ef311a63b8e48284e95fa3fd5.png",
    specs: [
      { icon: "ri-hd-line", label: "1080P Full HD" },
      { icon: "ri-signal-wifi-3-line", label: "4G LTE" },
      { icon: "ri-time-line", label: "13-hr Battery" },
      { icon: "ri-shield-check-line", label: "IP68" },
    ],
    highlights: ["130° wide-angle FOV", "120s pre-record", "Panic button", "Encrypted storage"],
    desc: "Reliable 4G body camera with 13-hour battery life — the workhorse for extended patrol shifts and security operations.",
  },
  {
    model: "GC550",
    badge: "Compact",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4c9b9e898793c8c887b16e91f8744d95.png",
    specs: [
      { icon: "ri-hd-line", label: "1080P HD" },
      { icon: "ri-scales-line", label: "Lightweight" },
      { icon: "ri-time-line", label: "8-hr Battery" },
      { icon: "ri-shield-check-line", label: "IP67" },
    ],
    highlights: ["120s pre-record buffer", "IR night vision", "Encrypted storage", "Compact clip-on design"],
    desc: "Compact and lightweight, the GC550 delivers reliable 1080P recording with infrared night vision — ideal for a wide range of security deployments.",
  },
];

const useCases = [
  {
    icon: "ri-shield-star-line",
    title: "Royal Malaysia Police (PDRM)",
    desc: "Frontline patrol officers equipped with SC880 5G for real-time command centre streaming, evidence capture, and public accountability.",
    stat: "4K + 5G Live",
  },
  {
    icon: "ri-building-2-line",
    title: "Government Enforcement Agencies",
    desc: "RELA, JPJ, and municipal enforcement teams using SC780 4G for field operations with tamper-proof evidence chain-of-custody.",
    stat: "Tamper-Proof",
  },
  {
    icon: "ri-hospital-line",
    title: "Hospital & Healthcare Security",
    desc: "Hospital security teams deploying SC780 4G for incident documentation, de-escalation evidence, and staff protection.",
    stat: "13-hr Battery",
  },
  {
    icon: "ri-store-2-line",
    title: "Retail & Loss Prevention",
    desc: "Shopping mall security and loss prevention teams using GC550 for comprehensive incident documentation.",
    stat: "Compact Design",
  },
  {
    icon: "ri-bank-line",
    title: "Banking & Financial Security",
    desc: "Bank security personnel using GC550 for discreet monitoring and incident recording in high-value environments.",
    stat: "Reliable Recording",
  },
  {
    icon: "ri-train-line",
    title: "Public Transport & Infrastructure",
    desc: "MRT, LRT, and bus rapid transit security teams using SC780 4G for coordinated incident response and evidence capture.",
    stat: "4G Connected",
  },
];

const whyHytera = [
  { icon: "ri-verified-badge-line", title: "Authorized Distributor", desc: "SE Distribution is the official authorized distributor for Hytera in Malaysia — full warranty, local support, and genuine products guaranteed." },
  { icon: "ri-customer-service-2-line", title: "Local Technical Support", desc: "Dedicated technical team in Kuala Lumpur for deployment, training, firmware updates, and after-sales support in English, Malay, and Chinese." },
  { icon: "ri-government-line", title: "Government Procurement Ready", desc: "Full documentation for government tender submissions — product datasheets, compliance certificates, and authorized distributor letters." },
  { icon: "ri-tools-line", title: "End-to-End Deployment", desc: "From needs assessment to device configuration, DEMS platform setup, and officer training — we handle the complete deployment lifecycle." },
];

const faqs = [
  { q: "What is a body worn camera and why do law enforcement agencies use them?", a: "A body worn camera (BWC) is a wearable video recording device used by law enforcement officers to capture audio and video evidence during incidents. They improve officer accountability, provide irrefutable evidence in court, reduce complaints against officers, and enhance public trust. Hytera BWC models like the SC880 and VM690 Pro are purpose-built for law enforcement with features like pre-record buffers, encrypted storage, and tamper-proof evidence management." },
  { q: "Which Hytera body camera is best for Malaysian police (PDRM)?", a: "For frontline PDRM patrol officers, we recommend the Hytera SC880 5G — it offers 4K video, 5G live streaming to command centres, 300-second pre-record, and MIL-STD-810G durability. For plainclothes or undercover operations, the VM580D TH (172g, PoC radio) or GC550 Mini (115g) are ideal. SE Distribution provides a free needs assessment to recommend the right model for your unit." },
  { q: "Does SE Distribution supply body cameras for government tenders in Malaysia?", a: "Yes. SE Distribution Sdn Bhd (Reg: 202001036939) is fully equipped to support government procurement and tender submissions for Hytera body-worn cameras. We provide authorized distributor letters, product compliance certificates, technical datasheets, and full after-sales support. Contact our sales team for tender documentation." },
  { q: "What is the Hytera DEMS evidence management platform?", a: "DEMS (Digital Evidence Management System) is Hytera's cloud-based platform for managing body camera footage. It provides automated upload from docking stations, chain-of-custody tracking, AI-powered search, audit trails, and role-based access control. DEMS ensures all evidence is tamper-proof and court-admissible." },
  { q: "How long do Hytera body cameras record on a single charge?", a: "Battery life varies by model: SC880 5G — 10 hours, SC780 4G — 13 hours, GC550 — 8 hours. Multi-bay docking stations allow overnight charging and automatic evidence upload to the DEMS platform." },
  { q: "Can Hytera body cameras stream live video to a command centre?", a: "Yes. The SC880 5G and SC780 4G support real-time live streaming over 4G/5G networks to a command centre or control room. This enables supervisors to monitor incidents in real-time and dispatch backup when needed. The SC880 5G supports the highest quality live stream at 4K/1080P over 5G." },
  { q: "Which Hytera body camera is best for Malaysian police (PDRM)?", a: "For frontline PDRM patrol officers, we recommend the Hytera SC880 5G — it offers 4K video, 5G live streaming to command centres, 300-second pre-record, and MIL-STD-810G durability. For general security deployments, the SC780 4G with 13-hour battery or the compact GC550 are excellent options. SE Distribution provides a free needs assessment to recommend the right model for your unit." },
];

const demoFormInitial = { name: "", company: "", email: "", phone: "", model: "", message: "" };

export default function BodyWornCameraPage() {
  useSEO({
    title: "Body Worn Camera Malaysia | Hytera SC880, SC780, GC550 | SE Distribution",
    description: "Malaysia's authorized Hytera body worn camera distributor. SC880 5G 4K flagship, SC780 4G 13-hr battery & GC550 compact — purpose-built for PDRM, government enforcement, and security teams. Full DEMS evidence management. Call +603 6412 7917.",
    keywords: "body worn camera Malaysia, body camera Malaysia, BWC Malaysia, Hytera body camera, SC880 5G Malaysia, SC780 4G Malaysia, GC550 body camera, police body camera Malaysia, law enforcement camera Malaysia, PDRM body camera",
    canonical: "https://www.sedistribution.com.my/body-worn-camera-malaysia",
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeProduct, setActiveProduct] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const [demoForm, setDemoForm] = useState(demoFormInitial);
  const [demoStatus, setDemoStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email || !demoForm.phone) return;
    setDemoStatus("sending");
    try {
      const body = new URLSearchParams({
        name: demoForm.name,
        company: demoForm.company,
        email: demoForm.email,
        phone: demoForm.phone,
        model: demoForm.model,
        message: demoForm.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d7iucj11u44cjefeorn0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setDemoStatus(res.ok ? "sent" : "error");
    } catch {
      setDemoStatus("error");
    }
  };

  useEffect(() => {
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.id = "bwc-schema";
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.sedistribution.com.my/body-worn-camera-malaysia",
          "url": "https://www.sedistribution.com.my/body-worn-camera-malaysia",
          "name": "Body Worn Camera Malaysia | Hytera BWC for Law Enforcement | SE Distribution",
          "description": "Malaysia's authorized Hytera body worn camera distributor. SC880 5G, VM690 Pro, SC780 4G for PDRM, government enforcement, and security teams.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
              { "@type": "ListItem", "position": 2, "name": "Body Worn Camera Malaysia", "item": "https://www.sedistribution.com.my/body-worn-camera-malaysia" }
            ]
          }
        },
        {
          "@type": "ItemList",
          "name": "Hytera Body Worn Camera Malaysia",
          "description": "Complete Hytera body worn camera lineup available in Malaysia from SE Distribution",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "Product", "name": "Hytera SC880 5G Body Camera", "description": "5G flagship body camera — 4K video, 300s pre-record, 10-hr battery, IP68, MIL-STD-810G", "brand": { "@type": "Brand", "name": "Hytera" }, "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "MYR", "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" } }, "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "47" } } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "Product", "name": "Hytera SC780 4G Body Camera", "description": "4G LTE smart body camera — 1080P, 130° FOV, 13-hr battery, 120s pre-record, IP68", "brand": { "@type": "Brand", "name": "Hytera" }, "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "MYR", "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" } }, "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "52" } } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "Product", "name": "Hytera GC550 Body Camera", "description": "Compact body camera — 1080P, 8-hr battery, IP67, infrared night vision, AES-256 encryption", "brand": { "@type": "Brand", "name": "Hytera" }, "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "MYR", "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" } }, "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.7", "reviewCount": "31" } } }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        }
      ]
    });
    document.head.appendChild(schemaScript);
    return () => { document.getElementById("bwc-schema")?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />
    <main>
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d1117 100%)" }}
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=law%20enforcement%20police%20officer%20wearing%20body%20worn%20camera%20on%20patrol%20urban%20city%20night%20scene%20dramatic%20lighting%20professional%20photography%20cinematic%20dark%20moody%20atmosphere&width=1440&height=700&seq=bwc-hero-bg&orientation=landscape"
            alt="Body worn camera law enforcement Malaysia"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 border border-white/20 px-3 py-1 rounded-full">Authorized Hytera Distributor · Malaysia</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Body Worn Camera<br />
              <span className="text-[#c8a96e]">Malaysia</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-2xl">
              Purpose-built Hytera body worn cameras for law enforcement, government enforcement agencies, and security teams across Malaysia. From PDRM patrol to plainclothes operations — we have the right body camera for every mission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#demo-form"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1a1a1a] font-semibold px-8 py-4 rounded-lg hover:bg-[#f5f3ee] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-customer-service-2-line"></i>
                Request a Demo
              </a>
              <Link
                to="/hytera-bwv"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-medium px-8 py-4 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Full Lineup
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { val: "3", label: "BWC Models" },
              { val: "4K", label: "Max Resolution" },
              { val: "13hr", label: "Max Battery" },
              { val: "IP68", label: "Water Rating" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-xl font-bold text-white">{s.val}</p>
                <p className="text-xs text-white/50">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#f8f7f4] border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { icon: "ri-verified-badge-line", text: "Authorized Hytera Distributor" },
            { icon: "ri-government-line", text: "Government Tender Ready" },
            { icon: "ri-customer-service-2-line", text: "Local KL Support Team" },
            { icon: "ri-shield-check-line", text: "Genuine Products · Full Warranty" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-[#1a1a1a] text-base`}></i>
              </div>
              <span className="text-xs font-medium text-[#1a1a1a] whitespace-nowrap">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Product Lineup */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Complete Lineup</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Hytera Body Worn Cameras</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
              Three purpose-built body worn camera models — from the 5G flagship SC880 to the reliable SC780 4G and the compact GC550. Every model features encrypted storage, tamper-proof evidence management, and MIL-SPEC durability.
            </p>
          </div>

          {/* Product tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {products.map((p, i) => (
              <button
                key={p.model}
                onClick={() => setActiveProduct(i)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeProduct === i
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-[#f5f3ee] text-[#1a1a1a] hover:bg-[#ede9e3]"
                }`}
              >
                {p.model}
              </button>
            ))}
          </div>

          {/* Active product detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto bg-[#f8f7f4] rounded-2xl overflow-hidden flex items-center justify-center p-8">
                <img
                  src={products[activeProduct].image}
                  alt={`Hytera ${products[activeProduct].model} body worn camera Malaysia`}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="absolute top-4 left-4 bg-[#1a1a1a] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                {products[activeProduct].badge}
              </div>
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">Hytera {products[activeProduct].model}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">{products[activeProduct].desc}</p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {products[activeProduct].specs.map((spec) => (
                  <div key={spec.label} className="flex items-center gap-3 bg-[#f8f7f4] rounded-lg p-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${spec.icon} text-[#1a1a1a] text-lg`}></i>
                    </div>
                    <span className="text-xs font-semibold text-[#1a1a1a]">{spec.label}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Key Features</p>
                <ul className="space-y-2">
                  {products[activeProduct].highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#1a1a1a] text-sm font-bold"></i>
                      </div>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Request Pricing
                </Link>
                <Link
                  to="/hytera-bwv"
                  className="inline-flex items-center justify-center gap-2 border border-gray-200 text-[#1a1a1a] font-medium px-6 py-3 rounded-lg hover:bg-[#f5f3ee] transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Full Specs
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* All products grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {products.map((p, i) => (
              <button
                key={p.model}
                onClick={() => setActiveProduct(i)}
                className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${
                  activeProduct === i
                    ? "border-[#1a1a1a] bg-[#f8f7f4]"
                    : "border-gray-100 hover:border-gray-200 bg-white"
                }`}
              >
                <div className="aspect-square bg-[#f5f3ee] rounded-lg mb-3 overflow-hidden flex items-center justify-center p-2">
                  <img src={p.image} alt={`Hytera ${p.model}`} className="w-full h-full object-contain" />
                </div>
                <p className="text-xs font-bold text-[#1a1a1a]">{p.model}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{p.badge}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* DEMS Platform */}
      <section className="py-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Evidence Management</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-5">Hytera DEMS Platform</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Body cameras are only as good as the evidence management system behind them. Hytera DEMS (Digital Evidence Management System) provides a complete chain-of-custody platform — from automatic upload at the docking station to court-ready evidence packages.
              </p>
              <div className="space-y-4">
                {[
                  { icon: "ri-upload-cloud-2-line", title: "Automatic Upload", desc: "Footage uploads automatically when cameras are docked — no manual intervention required." },
                  { icon: "ri-links-line", title: "Chain of Custody", desc: "Every access, edit, and export is logged with timestamp and user ID — fully court-admissible." },
                  { icon: "ri-search-eye-line", title: "AI-Powered Search", desc: "Search footage by date, officer, location, or incident type — find evidence in seconds." },
                  { icon: "ri-lock-password-line", title: "Encrypted & Tamper-Proof", desc: "AES-256 encryption with digital watermarking ensures footage integrity is preserved." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-white text-base`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">{item.title}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=digital%20evidence%20management%20system%20DEMS%20dashboard%20computer%20screen%20law%20enforcement%20police%20command%20center%20modern%20office%20professional%20technology%20interface%20dark%20theme&width=800&height=600&seq=bwc-dems&orientation=landscape"
                  alt="Hytera DEMS digital evidence management system Malaysia"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-bold text-[#1a1a1a] mb-1">DEMS Features</p>
                <div className="space-y-1">
                  {["Multi-bay docking station", "Role-based access control", "Audit trail & reporting", "Cloud or on-premise"].map((f) => (
                    <div key={f} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#1a1a1a] text-xs"></i>
                      </div>
                      <span className="text-[10px] text-gray-600">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Who We Serve</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Body Worn Camera Use Cases in Malaysia</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
              From frontline police patrol to hospital security — Hytera body worn cameras are deployed across Malaysia's most demanding environments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((uc) => (
              <div key={uc.title} className="bg-[#f8f7f4] rounded-xl p-6 border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
                    <i className={`${uc.icon} text-white text-xl`}></i>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border border-gray-200 px-2 py-1 rounded-full bg-white">{uc.stat}</span>
                </div>
                <h3 className="text-sm font-bold text-[#1a1a1a] mb-2">{uc.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why SE Distribution */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Malaysia's Trusted Hytera Partner</h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
              SE Distribution is not just a reseller — we are Hytera's authorized distributor in Malaysia with a dedicated team for deployment, training, and ongoing support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyHytera.map((item) => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request a Demo Form */}
      <section id="demo-form" className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Get Started</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                Request a Body Worn Camera Demo
              </h2>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                See Hytera body worn cameras in action — at our Kuala Lumpur office or on-site at your facility. Our team will demonstrate the full lineup, DEMS evidence management, and help you select the right model for your unit.
              </p>
              <div className="space-y-5">
                {[
                  { icon: "ri-camera-3-line", title: "Hands-on product demo", desc: "Try the SC880 5G, SC780 4G, and GC550 in person — feel the build quality and test the features." },
                  { icon: "ri-map-pin-line", title: "On-site or at our KL office", desc: "We come to you, or you visit our Kuala Lumpur showroom — whichever works best for your team." },
                  { icon: "ri-government-line", title: "Government tender support", desc: "We provide authorized distributor letters, compliance certificates, and full tender documentation." },
                  { icon: "ri-time-line", title: "Response within 1 business day", desc: "Our sales team responds to all demo requests within 1 business day." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-white text-base`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white mb-0.5">{item.title}</p>
                      <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-xs text-white/40 mb-3">Or contact us directly:</p>
                <div className="flex flex-col gap-2">
                  <a href="tel:+60364127917" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                    <i className="ri-phone-line text-white/40"></i>
                    +603 6412 7917
                  </a>
                  <a href="mailto:sales@sedistribution.com.my" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                    <i className="ri-mail-line text-white/40"></i>
                    sales@sedistribution.com.my
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl p-8">
              {demoStatus === "sent" ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#f8f7f4] rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-[#1a1a1a] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Demo Request Received!</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Thank you! Our team will contact you within 1 business day to schedule your Hytera body worn camera demo.
                  </p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleDemoSubmit} className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">Request a Demo</h3>
                    <p className="text-xs text-gray-400">We&apos;ll arrange a hands-on demo at your convenience.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={demoForm.name}
                        onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                        placeholder="Your name"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Organisation</label>
                      <input
                        type="text"
                        name="company"
                        value={demoForm.company}
                        onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                        placeholder="Agency / company"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={demoForm.email}
                        onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                        placeholder="+60 12 345 6789"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Model of Interest</label>
                    <select
                      name="model"
                      value={demoForm.model}
                      onChange={(e) => setDemoForm({ ...demoForm, model: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
                    >
                      <option value="">Select a model (optional)</option>
                      <option value="Hytera SC880 5G">Hytera SC880 5G — Flagship 4K 5G</option>
                      <option value="Hytera SC780 4G">Hytera SC780 4G — 13hr Battery</option>
                      <option value="Hytera GC550">Hytera GC550 — Compact</option>
                      <option value="Not sure - need recommendation">Not sure — need recommendation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Tell us about your deployment</label>
                    <textarea
                      name="message"
                      value={demoForm.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) setDemoForm({ ...demoForm, message: e.target.value });
                      }}
                      placeholder="Number of officers, use case, agency type, timeline..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 text-right">{demoForm.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={demoStatus === "sending"}
                    className="w-full bg-[#1a1a1a] text-white font-semibold py-3.5 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
                  >
                    {demoStatus === "sending" ? "Sending..." : "Request Demo"}
                  </button>
                  {demoStatus === "error" && (
                    <p className="text-xs text-red-500 text-center">Something went wrong. Please try again or call us directly.</p>
                  )}
                  <p className="text-[10px] text-gray-400 text-center">Mon–Fri, 9:30 AM – 6:30 PM (GMT+8) · No spam, ever.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Body Worn Camera Questions</h2>
            <p className="text-gray-500 text-sm">Common questions from law enforcement and security teams in Malaysia.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#fafaf9] transition-colors"
                >
                  <span className="text-sm font-semibold text-[#1a1a1a] pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-gray-400 text-base`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-50">
                    <p className="text-sm text-gray-500 leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#f8f7f4] border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-2xl mx-auto mb-6">
            <i className="ri-camera-3-line text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Ready to Deploy Body Worn Cameras?</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            Talk to our team about the right Hytera body worn camera for your unit — we handle everything from product selection to deployment and DEMS setup.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#demo-form"
              className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-customer-service-2-line"></i>
              Request a Demo
            </a>
            <a
              href="tel:+60364127917"
              className="inline-flex items-center justify-center gap-2 border border-gray-200 text-[#1a1a1a] font-medium px-8 py-4 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              +603 6412 7917
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-6">Mon–Fri, 9:30 AM – 6:30 PM (GMT+8) · sales@sedistribution.com.my</p>
        </div>
      </section>
      <StickyQuoteButton formId="demo-form" label="Request a Demo" />
      <Footer />
    </main>
    </div>
  );
}
